export const AI_AGENT_REPORT_TYPE = 'AI Agent';

export const AI_AGENT_OVERVIEW_SECTION_KEYS = {
    KEY_METRICS: 'keyMetrics',
    QUALITY_METRICS: 'qualityMetrics',
    TRENDS_AND_FEEDBACK: 'trendsAndFeedback',
    SUGGESTED_PROMPTS: 'suggestedPrompts',
    MODEL_USAGE: 'modelUsage',
    TOP_ENTITIES: 'topEntities',
    PROMPTS: 'prompts',
};

export const AI_AGENT_ICON_TYPE = 'agentic';

export const AI_AGENT_TYPES = {
    CONVERSATIONAL: 'conversational',
    MCP: 'mcp',
    AUTONOMOUS: 'autonomous',
};

export const TOOL_USAGE_VIEWS = {
    DONUT: 'donut',
    TABLE: 'table',
};

export const SIMILARITY_THRESHOLD = 0.6;

export const SUPPORT_LINK = 'https://support.pendo.io/hc/en-us/articles/41103585431707';

export const CONVERSATION_MODE_SUPPORT_LINK = 'https://support.pendo.io/hc/en-us/articles/41103585431707#h_01K5SBRA8V76QY9AGFMAAHZ4JM';

export const LEARN_MORE_LINK = 'https://support.pendo.io/hc/en-us/articles/45209760982683-Overview-of-Agent-Analytics';

export const SECURITY_LINK = 'https://support.pendo.io/hc/en-us/articles/41103184012315-Data-collection-and-security-for-Agent-Analytics-beta';

export const WIDGET_TYPE_NAME_MAP = {
    keyMetric: 'Key Metrics',
    useCaseTable: 'Emergent Use Cases',
    promptsTable: 'Prompts',
    trackedUseCasesTable: 'Tracked Use Cases',
    detectIssuesTable: 'Emergent Issues',
    qualityMetrics: 'AI quality metrics',
    trackedIssuesTable: 'Tracked Issues',
    feedbackChart: 'AI Agent feedback summary',
    toolsUsedChart: 'Tools used',
    modelsUsedChart: 'Models used',
    subagentsUsedChart: 'Subagents used',
    suggestedPromptsChart: 'Suggested prompts',
    topSuggestedPrompts: 'Top suggested prompts',
};

export const CLUSTER_BLADE_TYPE_MAP = {
    account: 'accounts',
    visitor: 'visitors',
    instances: 'instances',
    conversation: 'conversations',
    prompt: 'prompts',
    ragePrompt: 'rage prompts',
    error: 'errors',
    unsupported: 'unsupported requests',
    avgCost: 'conversations',
};

export const CLUSTER_BLADE_TYPES = ['useCase', 'trackedUseCases', 'detectIssues', 'trackedIssues', 'feedback', 'tools', 'suggestedPrompts'];

// Sampling threshold for AI agent use cases table
export const SAMPLING_THRESHOLD = 10000;

export const DEFAULT_CLUSTER_GRANULARITY = 3;

export const DEFAULT_ISSUE_CLUSTER_GRANULARITY = 5;

export const GRANULARITY_SLIDER_VARIANTS = {
    USE_CASE: 'useCase',
    ISSUE: 'issue',
};

export const USE_CASE_GRANULARITY_LEVEL_DESCRIPTIONS = {
    0: {
        name: 'Broad',
        description: 'Groups conversations by general themes or goals. Best for finding high-level trends.',
    },
    25: {
        name: 'General',
        description: 'Organizes conversations into high-level categories. Best for understanding user needs across key areas.',
    },
    50: {
        name: 'Balanced',
        description: 'Shows clear, distinct use cases. Best for identifying patterns without too much detail.',
    },
    75: {
        name: 'Focused',
        description: 'Highlights specific questions or tasks. Useful for refining known use cases or identifying gaps.',
    },
    100: {
        name: 'Granular',
        description: 'Breaks conversations into very detailed use cases. Ideal for deep analysis or reviewing edge cases.',
    },
};

export const ISSUE_GRANULARITY_LEVEL_DESCRIPTIONS = {
    0: {
        name: 'Broad',
        description: 'Groups issues into high-level themes. Best for spotting major problem areas.',
    },
    25: {
        name: 'General',
        description: 'Organizes issues into broader categories. Best for understanding where problems cluster.',
    },
    50: {
        name: 'Balanced',
        description: 'Shows clear, distinct issue types. Best for identifying patterns without too much detail.',
    },
    75: {
        name: 'Focused',
        description: 'Highlights specific issue types. Useful for refining known problems or finding gaps.',
    },
    100: {
        name: 'Granular',
        description: 'Breaks issues into very specific groupings. Ideal for deep analysis or reviewing edge cases.',
    },
};

export const GRANULARITY_LEVEL_DESCRIPTIONS_BY_VARIANT = {
    [GRANULARITY_SLIDER_VARIANTS.USE_CASE]: USE_CASE_GRANULARITY_LEVEL_DESCRIPTIONS,
    [GRANULARITY_SLIDER_VARIANTS.ISSUE]: ISSUE_GRANULARITY_LEVEL_DESCRIPTIONS,
};

export const AI_AGENT_GRANULARITY_USER_SETTINGS = {
    NAME: 'ai-agent-granularity-settings',
};

export const TRACKED_USE_CASE_TREND_COLOR_STEPS = [
    'var(--chart-volume-treemap-series-use-case-1-base)',
    'var(--chart-volume-treemap-series-use-case-2-base)',
    'var(--chart-volume-treemap-series-use-case-3-base)',
    'var(--chart-volume-treemap-series-use-case-4-base)',
    'var(--chart-volume-treemap-series-use-case-5-base)',
    'var(--chart-volume-treemap-series-use-case-6-base)',
    'var(--chart-volume-treemap-series-use-case-7-base)',
    'var(--chart-volume-treemap-series-use-case-8-base)',
    'var(--chart-volume-treemap-series-use-case-9-base)',
    'var(--chart-volume-treemap-series-use-case-10-base)',
];

export const TRACKED_ISSUE_TREND_COLOR_STEPS = [
    'var(--chart-volume-treemap-series-issue-1-base)',
    'var(--chart-volume-treemap-series-issue-2-base)',
    'var(--chart-volume-treemap-series-issue-3-base)',
    'var(--chart-volume-treemap-series-issue-4-base)',
    'var(--chart-volume-treemap-series-issue-5-base)',
    'var(--chart-volume-treemap-series-issue-6-base)',
    'var(--chart-volume-treemap-series-issue-7-base)',
    'var(--chart-volume-treemap-series-issue-8-base)',
    'var(--chart-volume-treemap-series-issue-9-base)',
    'var(--chart-volume-treemap-series-issue-10-base)',
];

export const TRACKED_TREND_ENTITY_FIELDS = {
    USE_CASE: {
        entityIdField: 'trackedUseCaseId',
        countField: 'numConversations',
    },
    ISSUE: {
        entityIdField: 'trackedIssueId',
        countField: 'numConversations',
    },
};

/** @type {Record<string, { prefix: string, title: string, topValues: string, conversations: string, emptyTitle: string, emptyDescription: string }>} */
export const TRACKED_TREND_CHART_I18N = {
    USE_CASE: {
        prefix: 'trackedUseCaseTrends',
        title: 'Tracked use case trends',
        topValues: 'Top {count} values',
        conversations: 'conversations',
        emptyTitle: 'No tracked use case trends yet',
        emptyDescription: 'No tracked use case conversations match your current filters. Trends appear once tracked use cases have conversation data.',
        filteredEmptyTitle: 'No tracked use cases match your filters',
        filteredEmptyDescription: 'Adjust your filters to see tracked use case trends.',
    },
    ISSUE: {
        prefix: 'trackedIssueTrends',
        title: 'Tracked issue trends',
        topValues: 'Top {count} values',
        conversations: 'conversations',
        emptyTitle: 'No tracked issue trends yet',
        emptyDescription: 'No tracked issue conversations match your current filters. Trends appear once tracked issues have conversation activity in the selected date range.',
        filteredEmptyTitle: 'No tracked issues match your filters',
        filteredEmptyDescription: 'Adjust your filters to see tracked issue trends.',
    },
};

export const TRACKED_TREND_CHART_CONFIG = {
    USE_CASE: {
        i18n: TRACKED_TREND_CHART_I18N.USE_CASE,
        userSettingsStorageKey: 'ai-agent-tracked-use-case-trends-chart-collapsed',
        scopedEntityIdsAggKey: 'scopedTrackedUseCaseIds',
        entityFields: TRACKED_TREND_ENTITY_FIELDS.USE_CASE,
        trendPaletteSteps: TRACKED_USE_CASE_TREND_COLOR_STEPS,
        trendBorderColor: 'var(--chart-volume-treemap-series-use-case-6-base)',
        tableType: 'trackedUseCases',
    },
    ISSUE: {
        i18n: TRACKED_TREND_CHART_I18N.ISSUE,
        userSettingsStorageKey: 'ai-agent-tracked-issue-trends-chart-collapsed',
        scopedEntityIdsAggKey: 'scopedTrackedIssueIds',
        entityFields: TRACKED_TREND_ENTITY_FIELDS.ISSUE,
        trendPaletteSteps: TRACKED_ISSUE_TREND_COLOR_STEPS,
        trendBorderColor: 'var(--chart-volume-treemap-series-issue-7-base)',
        tableType: 'trackedIssues',
    },
};

export const VOLUME_TREEMAP_ENTITY_FIELDS = {
    EMERGENT_USE_CASE: {
        labelField: 'clusterName',
        entityIdField: 'clusterId',
        volumeField: 'numConversations',
        fallbackVolumeField: 'numPrompts',
        secondaryMetricField: 'numVisitors',
    },
    EMERGENT_ISSUE: {
        labelField: 'clusterName',
        entityIdField: 'clusterId',
        volumeField: 'numConversationsPerIssue',
        fallbackVolumeField: 'numConversations',
        secondaryMetricField: 'numVisitors',
    },
};

/** @type {Record<string, { prefix: string, title: string, lowerVolume: string, higherVolume: string, promptCount: string, visitorCount: string, accessibilityDescription: string, emptyTitle: string, emptyDescription: string, minimumPromptsEmptyTitle: string, minimumPromptsEmptyDescriptionConversations: string, minimumPromptsEmptyDescriptionPrompts: string, filteredEmptyTitle: string, filteredEmptyDescription: string }>} */
export const VOLUME_TREEMAP_CHART_I18N = {
    EMERGENT_USE_CASE: {
        prefix: 'emergentUseCaseVolume',
        title: 'Emergent use case by volume',
        lowerVolume: 'Lower volume',
        higherVolume: 'Higher volume',
        promptCount: '{count, plural, one {# prompt} other {# prompts}}',
        visitorCount: '{count, plural, one {# visitor} other {# visitors}}',
        accessibilityDescription: 'Treemap showing emergent use cases sized and colored by conversation volume. Each tile represents one emergent use case with conversation and visitor counts.',
        emptyTitle: 'No emergent use case volume yet',
        emptyDescription: 'Volume will appear once your agent has enough conversation data.',
        minimumPromptsEmptyTitle: 'No emergent use cases available yet',
        minimumPromptsEmptyDescriptionConversations: 'Emergent use cases appear once your agent receives enough conversations in the selected date range.',
        minimumPromptsEmptyDescriptionPrompts: 'Emergent use cases appear once your agent receives enough prompts in the selected date range.',
        filteredEmptyTitle: 'No emergent use cases match your filters',
        filteredEmptyDescription: 'Adjust your filters to see emergent use case volume.',
    },
    EMERGENT_ISSUE: {
        prefix: 'emergentIssueVolume',
        title: 'Emergent issues by volume',
        lowerVolume: 'Lower volume',
        higherVolume: 'Higher volume',
        promptCount: '{count, plural, one {# prompt} other {# prompts}}',
        visitorCount: '{count, plural, one {# visitor} other {# visitors}}',
        accessibilityDescription: 'Treemap showing emergent issues sized and colored by conversation volume. Each tile represents one emergent issue with conversation and visitor counts.',
        emptyTitle: 'No emergent issue volume yet',
        emptyDescription: 'Volume will appear once your agent has enough conversation data.',
        minimumPromptsEmptyTitle: 'No emergent issues available yet',
        minimumPromptsEmptyDescriptionConversations: 'Emergent issues appear once your agent receives enough conversations in the selected date range.',
        minimumPromptsEmptyDescriptionPrompts: 'Emergent issues appear once your agent receives enough prompts in the selected date range.',
        filteredEmptyTitle: 'No emergent issues match your filters',
        filteredEmptyDescription: 'Adjust your filters to see emergent issue volume.',
    },
};

export const VOLUME_TREEMAP_CHART_CONFIG = {
    EMERGENT_USE_CASE: {
        i18n: VOLUME_TREEMAP_CHART_I18N.EMERGENT_USE_CASE,
        userSettingsStorageKey: 'ai-agent-emergent-use-case-volume-chart-collapsed',
        entityFields: VOLUME_TREEMAP_ENTITY_FIELDS.EMERGENT_USE_CASE,
        paletteSteps: TRACKED_USE_CASE_TREND_COLOR_STEPS,
        tableType: 'useCase',
    },
    EMERGENT_ISSUE: {
        i18n: VOLUME_TREEMAP_CHART_I18N.EMERGENT_ISSUE,
        userSettingsStorageKey: 'ai-agent-emergent-issue-volume-chart-collapsed',
        entityFields: VOLUME_TREEMAP_ENTITY_FIELDS.EMERGENT_ISSUE,
        paletteSteps: TRACKED_ISSUE_TREND_COLOR_STEPS,
        tableType: 'detectIssues',
    },
};

export const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

export const SETUP_BANNER_MIN_AGE_DAYS = 14;

export const SETUP_BANNER_MIN_AGE_MS = SETUP_BANNER_MIN_AGE_DAYS * MILLISECONDS_IN_DAY;

export const FIXTURE_BASED_IN_DASHBOARD_TEMPLATE_TYPES = ['useCaseTable', 'trackedUseCasesTable', 'detectIssuesTable', 'trackedIssuesTable'];

export const TRACKED_USE_CASE_STATUS = {
    STABLE: 'Stable',
    PROCESSING: 'Processing',
};

// Maximum number of active tracked use cases allowed per application
export const MAX_ACTIVE_TRACKED_USE_CASES_LIMIT = 200;

// Maximum number of detection rules allowed per tracked issue
export const MAX_TRACKED_ISSUE_RULES = 10;

// Maximum number of custom tracked intent metrics allowed per agent (matches backend cap)
export const CUSTOM_METRIC_MAX_PER_AGENT = 4;

export const CLUSTER_TABLE_SETTINGS = {
    NAME: 'ai-agent-clustering-table',
    DEFAULT_SORT: {
        prop: 'numVisitors',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
};

export const TRACKED_USE_CASE_TABLE_SETTINGS = {
    NAME: 'ai-agent-tracked-use-case-table',
    DEFAULT_SORT: {
        prop: 'numVisitors',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
};

export const TRACKED_ISSUES_TABLE_SETTINGS = {
    NAME: 'ai-agent-tracked-issues-table',
    DEFAULT_SORT: {
        prop: 'createdAt',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 50,
};

export const PROMPTS_TABLE_SETTINGS = {
    NAME: 'ai-agent-prompts-table',
    DEFAULT_SORT: {
        prop: 'browserTime',
        order: 'descending',
    },
    VISIBLE_COLUMNS: [],
    DEFAULT_PROMPTS_LIMIT: 1000,
};

export const DETECT_ISSUES_TABLE_SETTINGS = {
    NAME: 'ai-agent-detect-issues-table',
    DEFAULT_SORT: {
        prop: 'numVisitors',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
};

export const MESSAGE_TYPES = {
    USER: 'agentic:web:prompt',
    SYSTEM: 'agentic:web:agent_response',
    USER_REACTION: 'agentic:web:user_reaction',
    API_USER: 'agentic:api:prompt',
    API_SYSTEM: 'agentic:api:agent_response',
    API_USER_REACTION: 'agentic:api:user_reaction',
    API_TRACE: 'agentic:api:agent_trace',
};

// Arrays for checking multiple event types
export const USER_MESSAGE_TYPES = [MESSAGE_TYPES.USER, MESSAGE_TYPES.API_USER];

export const SYSTEM_MESSAGE_TYPES = [MESSAGE_TYPES.SYSTEM, MESSAGE_TYPES.API_SYSTEM];

export const USER_REACTION_MESSAGE_TYPES = [MESSAGE_TYPES.USER_REACTION, MESSAGE_TYPES.API_USER_REACTION];

// Helper functions for checking event types
export const isUserMessageType = (eventType) => USER_MESSAGE_TYPES.includes(eventType);

export const isSystemMessageType = (eventType) => SYSTEM_MESSAGE_TYPES.includes(eventType);

export const isUserReactionMessageType = (eventType) => USER_REACTION_MESSAGE_TYPES.includes(eventType);

// Filter expressions for aggregations, for use with the agenticEvents source
export const MESSAGE_TYPE_FILTERS = {
    USER: `(eventType == "${MESSAGE_TYPES.USER}" || eventType == "${MESSAGE_TYPES.API_USER}")`,
    SYSTEM: `(eventType == "${MESSAGE_TYPES.SYSTEM}" || eventType == "${MESSAGE_TYPES.API_SYSTEM}")`,
    USER_REACTION: `(eventType == "${MESSAGE_TYPES.USER_REACTION}" || eventType == "${MESSAGE_TYPES.API_USER_REACTION}")`,
};

export const RAGE_PROMPT_USER_FILTER = `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`;

// For use with the singleEvents source (session replay)
export const MESSAGE_TYPE_FILTERS_BY_TYPE = {
    USER: `(type == "${MESSAGE_TYPES.USER}" || type == "${MESSAGE_TYPES.API_USER}")`,
    SYSTEM: `(type == "${MESSAGE_TYPES.SYSTEM}" || type == "${MESSAGE_TYPES.API_SYSTEM}")`,
    USER_REACTION: `(type == "${MESSAGE_TYPES.USER_REACTION}" || type == "${MESSAGE_TYPES.API_USER_REACTION}")`,
};

export const FEEDBACK_TYPES = {
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
    UNREACT: 'unreact',
    // 'retry' is a regeneration request, not a reaction. The chat UI skips it
    // when resolving each message's displayed reaction, so a prior positive or
    // negative on the same messageId still surfaces.
    RETRY: 'retry',
};

export const CONVERSATIONS_TABLE_SETTINGS = {
    NAME: 'ai-agent-conversations-table',
    DEFAULT_SORT: {
        prop: 'createdAt',
        order: 'descending',
    },
    PAGE_NUMBER: 1,
    PAGE_SIZE: 10,
    DEFAULT_CONVERSATION_LIMIT: 1000,
};

export const OVER_TIME_CHART_SETTINGS = {
    NAME: 'ai-agent-over-time-chart',
};

export const KEY_METRICS_GRID_SETTINGS = {
    NAME: 'ai-agent-key-metrics-grid',
};

export const SEVERITY_THRESHOLDS = [
    { max: 1.0, level: 'critical' },
    { max: 2.0, level: 'major' },
    { max: 3.0, level: 'moderate' },
];

export const SEVERITY_LEVEL_TEXT_MAP = {
    critical: 'severityScore.Critical',
    major: 'severityScore.Major',
    moderate: 'severityScore.Moderate',
};

export const API_AGENTIC_EVENT_SERVER = '__apiagenticevent__';

// Canonical, ordered list of redaction class ids the backend supports (mirrors
// pendo.io/redaction/types.RedactionClassList). Labels/tooltips live as $t() calls in
// ai-agent-pii-settings.vue so the i18n extraction tooling can find them.
export const REDACTION_CLASSES = [
    'names',
    'userNames',
    'emails',
    'phoneNumbers',
    'addresses',
    'dates',
    'ssn',
    'nationalIds',
    'taxIds',
    'creditDebitCards',
    'bankAcctInfo',
    'invoiceNumbers',
    'currencyAmounts',
    'ipAddresses',
];

// Mirrors the backend's max input length for POST .../redaction/test (APP-164056).
export const MAX_REDACTION_PREVIEW_LENGTH = 4000;
